import {
  format,
  parseISO,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isWithinInterval,
  isSameDay,
  differenceInDays,
  max as dateMax,
  min as dateMin,
} from 'date-fns';

export function formatDate(dateStr: string, fmt: string = 'MMM d'): string {
  return format(parseISO(dateStr), fmt);
}

export function formatDateFull(dateStr: string): string {
  return format(parseISO(dateStr), 'EEEE, MMMM d');
}

export function formatDateShort(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr;
  }
}

export function toDateStr(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function getDaysInMonth(year: number, month: number): string[] {
  const start = startOfMonth(new Date(year, month - 1));
  const end = endOfMonth(start);
  return eachDayOfInterval({ start, end }).map(toDateStr);
}

export function getNextMonth(year: number, month: number): { year: number; month: number } {
  const d = addMonths(new Date(year, month - 1), 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function getPrevMonth(year: number, month: number): { year: number; month: number } {
  const d = subMonths(new Date(year, month - 1), 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function getMonthLabel(year: number, month: number): string {
  return format(new Date(year, month - 1), 'MMMM yyyy');
}

export function dateRangeOverlap(
  startA: string,
  endA: string,
  startB: string,
  endB: string
): { start: string; end: string } | null {
  const a1 = parseISO(startA);
  const a2 = parseISO(endA);
  const b1 = parseISO(startB);
  const b2 = parseISO(endB);

  const overlapStart = dateMax([a1, b1]);
  const overlapEnd = dateMin([a2, b2]);

  if (overlapStart > overlapEnd) return null;

  return {
    start: toDateStr(overlapStart),
    end: toDateStr(overlapEnd),
  };
}

export function daysBetween(startDate: string, endDate: string): number {
  return differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
}

export function isDateInRange(dateStr: string, startStr: string, endStr: string): boolean {
  const date = parseISO(dateStr);
  return isWithinInterval(date, { start: parseISO(startStr), end: parseISO(endStr) });
}

export function isSameDateStr(a: string, b: string): boolean {
  return isSameDay(parseISO(a), parseISO(b));
}

export { parseISO, format, eachDayOfInterval, addMonths, subMonths };
